import { ref } from 'vue'
import { fetchVideoAsset } from '@streampac.io/chef_sp_1'
import { type IVideoAsset } from '@/types/VideoAsset'
import { isAuthenticated } from '@/utils/global'

interface VideoAsset {
  [key: string]: IVideoAsset
}

const data = ref<VideoAsset>({})
const requestedMediaIds = ref(new Set())

export interface useVideoAssetModule {
  data: typeof data
  getVideoAsset: (mediaId: string | string[]) => Promise<void>
}

export const useVideoAsset: () => useVideoAssetModule = () => {
  const CONCURRENT_LIMIT = 30 // Adjust based on your server's capacity and client's network

  // Function to delay execution
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  // Function to fetch a single video asset with retry mechanism
  const fetchWithRetry = async (id: string, retries: number = 2, retryDelay: number = 300) => {
    try {
      const response = await fetchVideoAsset(isAuthenticated.value, id)
      return response
    } catch (error) {
      if (retries > 0) {
        await delay(retryDelay)
        return fetchWithRetry(id, retries - 1, retryDelay)
      } else {
        //throw error;
      }
    }
  }

  // Modified fetchVideoAssets function
  async function fetchVideoAssets(mediaIds: string[]) {
    for (let i = 0; i < mediaIds.length; i += CONCURRENT_LIMIT) {
      const batch = mediaIds.slice(i, i + CONCURRENT_LIMIT)
      await Promise.allSettled(
        batch.map(async (id: string) => {
          requestedMediaIds.value.add(id)
          try {
            const response = await fetchWithRetry(id)
            data.value[id] = response
            // console.log(response);
          } catch (error) {
            //console.error(`Error fetching video asset for id ${id}:`, error);
          } finally {
            requestedMediaIds.value.delete(id)
          }
        })
      )
      if (i + CONCURRENT_LIMIT < mediaIds.length) {
        // Only delay if there are more batches to process
        await delay(200)
      }
    }
  }

  const getVideoAsset = async (mediaId: string | string[]) => {
    const requestMediaIds = Array.isArray(mediaId) ? mediaId : [mediaId]
    const filteredIds = requestMediaIds.filter(
      (id) => !requestedMediaIds.value.has(id) && !data.value[id]
    )
    if (filteredIds.length) {
      await fetchVideoAssets(filteredIds)
    }
  }

  return {
    data,
    getVideoAsset
  }
}
